import React from 'react';
import { LucideIcon } from 'lucide-react';
import Skeleton from '../ui/Skeleton';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  change?: {
    value: number;
    label: string;
    trend: 'up' | 'down' | 'neutral';
  };
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'orange';
  subValues?: Array<{ label: string; value: string | number }>;
  onClick?: () => void;
  ariaLabel?: string;
  loading?: boolean;
}

/** Matches text-xl / text-2xl / text-3xl with leading-none. */
const VALUE_ROW_HEIGHT = 'h-7 sm:h-8 xl:h-9';
const VALUE_TEXT =
  'text-xl sm:text-2xl xl:text-3xl font-bold leading-none tabular-nums text-gray-900';
const SUB_VALUE_TEXT =
  'text-[11px] sm:text-sm font-semibold leading-none tabular-nums text-gray-900';
/** Stable slot for "↗ +12% vs 6 mars" on wide cards (xl+). */
const CHANGE_SLOT_WIDTH = 'xl:min-w-[8rem]';

/** Icon scales with card height, capped per breakpoint. */
const ICON_COLUMN =
  'w-[4.25rem] shrink-0 sm:w-[4.75rem] xl:w-20';
const ICON_BOX =
  'aspect-square h-[min(100%,4.25rem)] w-full max-w-full sm:h-[min(100%,4.75rem)] xl:h-[min(100%,5rem)]';
const ICON_GLYPH = 'h-[46%] w-[46%]';

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon: Icon,
  change,
  color = 'blue',
  subValues,
  onClick,
  ariaLabel,
  loading = false,
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  const trendClasses = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-600',
  };

  const clickable = typeof onClick === 'function';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  const changeContent = change ? (
    <div className={`flex min-w-0 items-center gap-x-1 sm:gap-x-1.5 ${CHANGE_SLOT_WIDTH}`}>
      <span className={`text-xs sm:text-sm font-semibold tabular-nums leading-none shrink-0 ${trendClasses[change.trend]}`}>
        {change.trend === 'up' && '↗'}
        {change.trend === 'down' && '↘'}
        {change.value > 0 && change.trend !== 'down' && '+'}
        {change.value}%
      </span>
      <span className="text-xs leading-none text-gray-500 truncate">{change.label}</span>
    </div>
  ) : null;

  const iconTransition = clickable ? 'transition-transform duration-200 group-hover:scale-105' : '';

  return (
    <div
      className={`group bg-white p-3 sm:p-4 xl:p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 h-full ${clickable ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      <div className="flex h-full gap-3 sm:gap-4 xl:gap-5">
        {/* Left column: icon only, centered on full card height */}
        <div className={`flex items-center justify-center self-stretch ${ICON_COLUMN}`}>
          <div
            className={`flex items-center justify-center rounded-full ${ICON_BOX} ${colorClasses[color]} ${iconTransition}`}
          >
            <Icon className={ICON_GLYPH} strokeWidth={2} />
          </div>
        </div>

        {/* Right column: main block + sub-values */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="min-w-0">
            <p className="mb-1 truncate text-xs font-medium uppercase tracking-wide text-gray-600 sm:mb-1.5 sm:text-sm">
              {title}
            </p>

            <div className="flex flex-col gap-y-0.5 sm:flex-row sm:items-center sm:gap-x-3 sm:gap-y-0">
              <div className={`flex shrink-0 items-center ${VALUE_ROW_HEIGHT}`}>
                {loading ? (
                  <Skeleton className="h-full w-[3ch] rounded sm:w-[4ch] xl:w-[5ch]" />
                ) : (
                  <span className={VALUE_TEXT}>{value}</span>
                )}
              </div>
              {change ? (
                <div className="flex min-w-0 items-center sm:shrink-0">
                  {loading ? (
                    <Skeleton className="h-3.5 w-full rounded sm:h-4 xl:w-[8rem]" />
                  ) : (
                    changeContent
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {subValues && subValues.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-200/40 pt-2 sm:mt-2.5 sm:gap-x-4 sm:pt-2.5 xl:gap-x-5">
              {subValues.map((s) => (
                <div
                  key={String(s.label)}
                  className="flex min-w-0 items-center gap-x-1 sm:gap-x-1.5"
                >
                  <span className="shrink-0 whitespace-nowrap text-[11px] font-medium leading-none text-gray-500 sm:text-xs">
                    {s.label}
                  </span>
                  <div className="flex h-4 items-center sm:h-5">
                    {loading ? (
                      <Skeleton className="h-full w-[1.75rem] rounded sm:w-[2rem]" />
                    ) : (
                      <span className={`${SUB_VALUE_TEXT} whitespace-nowrap`}>{s.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricCard;
